import { Link } from 'react-router-dom';
import { ArrowRight, Heart, Calendar, BookOpen } from 'lucide-react';
import { churchInfo, services, salvationSteps, sinMessage, sinnersPrayer, fruitsOfSpirit, tenCommandments } from '../mock';
import { Card, CardContent } from '../components/ui/card';
import { useState, useEffect } from 'react';

const CAROUSEL_PHOTOS = [
  { id: 'family-3', url: "/images/family3.jpg", alt: "Church Family Fellowship" },
  { id: 'ministers', url: "/images/ministers.jpg", alt: "Church Ministers" },
  { id: 'family-5', url: "/images/family5.jpg", alt: "Church Family" },
  { id: 'family-2', url: "/images/family2.jpg", alt: "Church Family Fellowship 2" },
  { id: 'interior', url: "/images/interior.jpg", alt: "Church Sanctuary" },
  { id: 'exterior', url: "/images/exterior.jpg", alt: "Church Exterior" },
];

const CAROUSEL_INTERVAL_MS = 4000;

const PhotoCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % CAROUSEL_PHOTOS.length);
    }, CAROUSEL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="relative w-full h-[500px] overflow-hidden rounded-2xl shadow-2xl">
      {CAROUSEL_PHOTOS.map((photo, index) => (
        <div
          key={photo.id}
          className={`absolute w-full h-full transition-opacity duration-1000 ${
            index === currentIndex ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img src={photo.url} alt={photo.alt} className="w-full h-full object-cover" />
        </div>
      ))}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
        {CAROUSEL_PHOTOS.map((photo, index) => (
          <button
            key={photo.id}
            onClick={() => setCurrentIndex(index)}
            className={`w-3 h-3 rounded-full transition-all duration-300 ${
              index === currentIndex ? 'bg-yellow-400 w-8' : 'bg-white/50 hover:bg-white/75'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export const Home = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section with Dove */}
      <section className="relative h-[600px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1555642870-8bcf4c3599b3"
            alt="White Dove"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-blue-50/80 via-white/70 to-white/90" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl sm:text-6xl md:text-8xl lg:text-9xl font-bold text-blue-800 mb-6 animate-fade-in whitespace-nowrap">
            Welcome to True Love
          </h1>
          <p className="text-3xl md:text-4xl text-yellow-600 mb-4 font-semibold">
            {churchInfo.tagline}
          </p>
          <p className="text-2xl text-blue-700 mb-8 max-w-2xl mx-auto">
            Join us for worship and fellowship at {churchInfo.name}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/about"
              className="px-8 py-4 bg-blue-500 text-white rounded-lg font-semibold hover:bg-blue-600 transition-all duration-200 hover:shadow-lg transform hover:-translate-y-0.5 flex items-center justify-center space-x-2"
            >
              <span>Learn More</span>
              <ArrowRight className="h-5 w-5" />
            </Link>
            <Link
              to="/ministries"
              className="px-8 py-4 bg-yellow-500 text-blue-900 rounded-lg font-semibold hover:bg-yellow-400 transition-all duration-200 hover:shadow-lg transform hover:-translate-y-0.5"
            >
              Our Ministries
            </Link>
          </div>
        </div>
      </section>

      {/* Service Times */}
      <section className="py-16 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-6xl md:text-7xl font-bold text-blue-800 mb-4">Join Us For Worship</h2>
            <p className="text-xl text-blue-700">Everyone is welcome in God&apos;s house</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {services.map((service) => (
              <Card key={service.id} className="border-2 border-yellow-400 hover:border-yellow-500 transition-colors duration-200 hover:shadow-xl bg-white">
                <CardContent className="p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="bg-blue-100 p-3 rounded-lg">
                      <Calendar className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-3xl font-semibold text-blue-800">{service.name}</h3>
                      <p className="text-yellow-600 font-semibold text-lg">{service.day} at {service.time}</p>
                    </div>
                  </div>
                  <p className="text-blue-700">{service.description}</p>
                  {service.zoomLink && (
                    <a
                      href={service.zoomLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`service-zoom-link-${service.id}`}
                      className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 underline font-semibold mt-2"
                    >
                      Join via Zoom
                    </a>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Photo Carousel */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-6xl md:text-7xl font-bold text-blue-800 mb-4">Our Church Family</h2>
            <p className="text-xl text-blue-700">Experience the love and fellowship at True Love</p>
          </div>
          <PhotoCarousel />
        </div>
      </section>

      {/* Do You Know Jesus - Salvation Steps */}
      <section className="py-16 bg-gradient-to-br from-yellow-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-6xl md:text-7xl font-bold text-blue-800 mb-4">Do You Know Jesus?</h2>
            <p className="text-xl text-blue-700">Three simple steps to salvation</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {salvationSteps.map((step) => (
              <Card key={step.id} className="bg-white hover:shadow-xl transition-shadow duration-200 border-2 border-blue-200">
                <CardContent className="p-8">
                  <div className="bg-yellow-500 w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto">
                    <span className="text-white font-bold text-3xl">{step.id}</span>
                  </div>
                  <h3 className="text-4xl font-semibold text-blue-800 mb-4 text-center">{step.title}</h3>
                  <p className="text-blue-700 mb-4 text-center font-semibold">{step.description}</p>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm text-yellow-600 font-bold mb-2">{step.scripture}</p>
                    <p className="text-sm text-blue-800 italic">{step.verse}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Sin Message */}
          <Card className="bg-white border-2 border-blue-200 mb-8">
            <CardContent className="p-8">
              <h3 className="text-5xl font-semibold text-blue-800 mb-4 text-center">{sinMessage.title}</h3>
              <p className="text-lg text-blue-700 leading-relaxed text-center max-w-4xl mx-auto">
                {sinMessage.content}
              </p>
            </CardContent>
          </Card>

          {/* Sinner's Prayer */}
          <Card className="bg-gradient-to-br from-blue-500 to-blue-700 text-white border-4 border-yellow-400">
            <CardContent className="p-8">
              <div className="text-center mb-6">
                <Heart className="h-16 w-16 text-yellow-400 mx-auto mb-4" />
                <h3 className="text-5xl font-semibold mb-6">{sinnersPrayer.title}</h3>
              </div>
              <p className="text-xl leading-relaxed italic max-w-3xl mx-auto text-center">
                {sinnersPrayer.prayer}
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Fruits of the Spirit */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-6xl md:text-7xl font-bold text-blue-800 mb-4">{fruitsOfSpirit.title}</h2>
            <p className="text-xl text-blue-700 mb-2">{fruitsOfSpirit.intro}</p>
            <p className="text-lg text-yellow-600 font-semibold">({fruitsOfSpirit.scripture})</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {fruitsOfSpirit.fruits.map((fruit) => (
              <Card key={fruit} className="bg-gradient-to-br from-blue-50 to-yellow-50 border-2 border-blue-200 hover:border-yellow-400 transition-all duration-200 hover:shadow-lg">
                <CardContent className="p-6 text-center">
                  <p className="text-2xl font-semibold text-blue-800">{fruit}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Ten Commandments */}
      <section className="py-16 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-6xl md:text-7xl font-bold text-blue-800 mb-4">{tenCommandments.title}</h2>
            <p className="text-lg text-blue-700 max-w-4xl mx-auto mb-8">{tenCommandments.intro}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {tenCommandments.commandments.map((commandment, idx) => (
              <Card key={commandment} className="bg-white border-2 border-yellow-400 hover:shadow-lg transition-shadow duration-200">
                <CardContent className="p-6 flex items-start space-x-4">
                  <div className="bg-blue-500 text-white w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="font-bold text-xl">{idx + 1}</span>
                  </div>
                  <p className="text-lg text-blue-800 pt-2">{commandment}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-blue-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <BookOpen className="h-16 w-16 text-yellow-400 mx-auto mb-6" />
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-4">Become Part of Our Family</h2>
          <p className="text-2xl text-blue-100 mb-8">
            We&apos;d love to have you join us for worship. Come as you are and experience the love of Christ.
          </p>
          <Link
            to="/ministries"
            className="inline-flex items-center space-x-2 px-8 py-4 bg-yellow-500 text-blue-900 rounded-lg font-bold hover:bg-yellow-400 transition-all duration-200 hover:shadow-lg transform hover:-translate-y-0.5 text-lg"
          >
            <span>Explore Our Ministries</span>
            <ArrowRight className="h-6 w-6" />
          </Link>
        </div>
      </section>
    </div>
  );
};
